import React from "react";
import { useEntitlements } from "../../hooks/useEntitlements";
import { Feature } from "../../types/newtypes";
import { Loading, Typography } from "./index";
import { Lock } from "lucide-react";

interface FeatureGateProps {
  /**
   * Feature(s) required to show the children.
   * If array, all features must be present (AND logic).
   */
  feature: Feature | Feature[];

  /**
   * If true, show children when user has ANY of the specified features (OR logic).
   * Default is false (AND logic - all features required).
   */
  any?: boolean;

  /**
   * Content to show when user has the required feature(s)
   */
  children: React.ReactNode;

  /**
   * Content to show when user doesn't have access.
   * If not provided, nothing is rendered (silent gate).
   */
  fallback?: React.ReactNode;

  /**
   * If true, shows a loading state while entitlements are being fetched.
   * Default is true.
   */
  showLoading?: boolean;

  /**
   * If true, shows a styled "upgrade required" fallback.
   * Only used if no custom fallback is provided.
   */
  showUpgradePrompt?: boolean;
}

/**
 * Component to conditionally render content based on user entitlements.
 *
 * @example
 * // Single feature gate
 * <FeatureGate feature="ADMIN_CONSOLE">
 *   <AdminDashboard />
 * </FeatureGate>
 *
 * @example
 * // Multiple features (AND logic - all required)
 * <FeatureGate feature={["ADMIN_CONSOLE", "COMPLIANCE_EXPORTS"]}>
 *   <ExportPanel />
 * </FeatureGate>
 *
 * @example
 * // Multiple features (OR logic - any one required)
 * <FeatureGate feature={["COURSE_ACCESS", "ADVANCED_ANALYTICS"]} any>
 *   <PremiumContent />
 * </FeatureGate>
 *
 * @example
 * // With custom fallback
 * <FeatureGate
 *   feature="COURSE_CERTIFICATES"
 *   fallback={<UpgradePrompt />}
 * >
 *   <CertificateViewer />
 * </FeatureGate>
 */
const FeatureGate: React.FC<FeatureGateProps> = ({
  feature,
  any = false,
  children,
  fallback,
  showLoading = true,
  showUpgradePrompt = false,
}) => {
  const { hasAnyFeature, hasAllFeatures, isLoading, entitlements } =
    useEntitlements();

  // Show loading state
  if (isLoading && showLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loading />
      </div>
    );
  }

  // No entitlements yet (not logged in or error)
  if (!entitlements) {
    return fallback ? <>{fallback}</> : null;
  }

  // Check feature access
  const features = Array.isArray(feature) ? feature : [feature];
  const hasAccess = any
    ? hasAnyFeature(...features)
    : hasAllFeatures(...features);

  // User has access - render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // No access - render fallback or nothing
  if (fallback) {
    return <>{fallback}</>;
  }

  // Show upgrade prompt if requested
  if (showUpgradePrompt) {
    return <UpgradePrompt />;
  }

  // Silent gate - render nothing
  return null;
};

/**
 * Default upgrade prompt component
 */
const UpgradePrompt: React.FC = () => {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center">
      <div className="mx-auto w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center mb-4">
        <Lock className="text-slate-500" size={24} />
      </div>
      <Typography variant="h5" weight="semibold" className="text-textPrimary mb-2">
        Premium Feature
      </Typography>
      <Typography variant="p" className="text-textSecondary mb-4">
        This feature is not available on your current plan. Upgrade to access
        advanced features and unlock your team's full potential.
      </Typography>
      <a
        href="mailto:support@pedagogue.io?subject=Plan%20Upgrade%20Inquiry"
        className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors"
      >
        Contact Sales
      </a>
    </div>
  );
};

export default FeatureGate;
